/**
 * 角色詳情（權限管理）。
 *
 * 模式（mode）：
 *   - view：唯讀檢視（meta + 權限依 module 分組）
 *   - perms：權限矩陣編輯（切片 2）→ PUT /admin/roles/:id/permissions
 *   - meta：角色資訊編輯（切片 4）→ PATCH /admin/roles/:id
 * 另：刪除自訂角色（切片 4）→ DELETE /admin/roles/:id（系統角色不顯示刪除）。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  deleteRole,
  getPermissionCatalog,
  getRole,
  setRolePermissions,
  updateRole,
  type RbacPermission,
} from '../lib/api-client';

type Mode = 'view' | 'perms' | 'meta';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

export function RoleDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('view');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [metaForm, setMetaForm] = useState({
    display_name: '',
    description: '',
    require_2fa: false,
  });

  const roleQuery = useQuery({
    queryKey: ['admin.roles', id],
    queryFn: () => getRole(id),
    enabled: id.length > 0,
  });
  const catalogQuery = useQuery({ queryKey: ['admin.permissions'], queryFn: getPermissionCatalog });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin.roles'] });
    void queryClient.invalidateQueries({ queryKey: ['admin.roles', id] });
  };

  const permMutation = useMutation({
    mutationFn: (permissionIds: string[]) => setRolePermissions(id, permissionIds),
    onSuccess: () => {
      invalidate();
      setMode('view');
    },
  });
  const metaMutation = useMutation({
    mutationFn: () =>
      updateRole(id, {
        display_name: metaForm.display_name.trim(),
        description: metaForm.description.trim(),
        require_2fa: metaForm.require_2fa,
      }),
    onSuccess: () => {
      invalidate();
      setMode('view');
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteRole(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin.roles'] });
      navigate('/admin/roles');
    },
  });

  const role = roleQuery.data;

  const groupedOwned = useMemo<[string, RbacPermission[]][]>(() => {
    const byModule = new Map<string, RbacPermission[]>();
    for (const p of role?.permissions ?? []) {
      const arr = byModule.get(p.module);
      if (arr) arr.push(p);
      else byModule.set(p.module, [p]);
    }
    return [...byModule.entries()];
  }, [role]);

  const enterPerms = (): void => {
    setSelected(new Set((role?.permissions ?? []).map((p) => p.id)));
    permMutation.reset();
    setMode('perms');
  };
  const enterMeta = (): void => {
    setMetaForm({
      display_name: role?.display_name ?? '',
      description: role?.description ?? '',
      require_2fa: role?.require_2fa ?? false,
    });
    metaMutation.reset();
    setMode('meta');
  };
  const toggle = (permId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };
  const handleDelete = (): void => {
    if (!role) return;
    if (
      window.confirm(
        `確定刪除角色「${role.display_name}」（${role.key}）？\n此操作不可復原，並會寫入 audit log。`,
      )
    ) {
      deleteMutation.mutate();
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">角色詳情</h1>
        <div className="page-head__actions">
          <Link to="/admin/roles">
            <Button variant="ghost">← 返回角色列表</Button>
          </Link>
          {mode === 'view' && (
            <>
              <Button
                variant="secondary"
                onClick={() => roleQuery.refetch()}
                disabled={roleQuery.isFetching}
              >
                {roleQuery.isFetching ? '更新中…' : '重新整理'}
              </Button>
              <Button variant="secondary" onClick={enterMeta} disabled={!role}>
                編輯資訊
              </Button>
              <Button variant="primary" onClick={enterPerms} disabled={!role}>
                編輯權限
              </Button>
              {role && !role.is_system && (
                <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? '刪除中…' : '刪除角色'}
                </Button>
              )}
            </>
          )}
          {mode === 'perms' && (
            <>
              <Button
                variant="secondary"
                onClick={() => setMode('view')}
                disabled={permMutation.isPending}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={() => permMutation.mutate([...selected])}
                disabled={permMutation.isPending}
              >
                {permMutation.isPending ? '儲存中…' : `儲存（${selected.size} 項權限）`}
              </Button>
            </>
          )}
          {mode === 'meta' && (
            <>
              <Button
                variant="secondary"
                onClick={() => setMode('view')}
                disabled={metaMutation.isPending}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={() => metaMutation.mutate()}
                disabled={metaMutation.isPending || metaForm.display_name.trim().length === 0}
              >
                {metaMutation.isPending ? '儲存中…' : '儲存資訊'}
              </Button>
            </>
          )}
        </div>
      </div>

      {roleQuery.isLoading && <div>載入中…</div>}
      {roleQuery.error && <div className="error-banner">{(roleQuery.error as Error).message}</div>}
      {(permMutation.error || metaMutation.error || deleteMutation.error) && (
        <div className="error-banner">
          操作失敗：
          {((permMutation.error || metaMutation.error || deleteMutation.error) as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：角色寫操作需 admin.rbac_manage 權限（僅 SuperAdmin 預設持有）。
          </div>
        </div>
      )}

      {role && (
        <>
          {mode === 'meta' ? (
            <div className="card" style={{ marginBottom: 16, maxWidth: 560 }}>
              <h2 style={{ fontSize: 15, margin: '0 0 10px 0' }}>編輯角色資訊</h2>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
                  角色 key：<code>{role.key}</code>（建立後不可變更）
                </div>
                <label style={{ fontSize: 13 }}>
                  顯示名稱
                  <input
                    type="text"
                    value={metaForm.display_name}
                    onChange={(e) => setMetaForm((f) => ({ ...f, display_name: e.target.value }))}
                    style={{ width: '100%', padding: 6 }}
                  />
                </label>
                <label style={{ fontSize: 13 }}>
                  說明
                  <input
                    type="text"
                    value={metaForm.description}
                    onChange={(e) => setMetaForm((f) => ({ ...f, description: e.target.value }))}
                    style={{ width: '100%', padding: 6 }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={metaForm.require_2fa}
                    onChange={(e) => setMetaForm((f) => ({ ...f, require_2fa: e.target.checked }))}
                  />
                  強制 2FA
                </label>
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 16 }}>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '6px 16px',
                  margin: 0,
                }}
              >
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>角色 key</dt>
                <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>{role.key}</dd>
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>顯示名稱</dt>
                <dd style={{ margin: 0 }}>{role.display_name}</dd>
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>說明</dt>
                <dd style={{ margin: 0 }}>{role.description ?? '—'}</dd>
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>類型</dt>
                <dd style={{ margin: 0 }}>
                  {role.is_system ? (
                    <Pill tone="brand">系統角色（不可刪除）</Pill>
                  ) : (
                    <Pill tone="neutral">自訂角色</Pill>
                  )}
                </dd>
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>2FA</dt>
                <dd style={{ margin: 0 }}>
                  {role.require_2fa ? (
                    <Pill tone="warning">強制 2FA — 持此角色之使用者登入須通過 TOTP</Pill>
                  ) : (
                    <Pill tone="neutral">免 2FA</Pill>
                  )}
                </dd>
                <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>建立時間</dt>
                <dd style={{ margin: 0, fontSize: 13 }}>{formatDateTime(role.created_at)}</dd>
              </dl>
            </div>
          )}

          {mode === 'perms' ? (
            <>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>編輯權限</h2>
              <p className="page-intro" style={{ marginTop: 0 }}>
                勾選此角色應擁有之權限；儲存為覆蓋式。權限變更於持此角色之使用者
                <strong>下次登入</strong>後生效（JWT scope 於登入時烙印）。
              </p>
              {catalogQuery.isLoading && <div>載入權限目錄…</div>}
              {catalogQuery.error && (
                <div className="error-banner">{(catalogQuery.error as Error).message}</div>
              )}
              {catalogQuery.data?.modules.map((m) => (
                <div key={m.module} style={{ marginBottom: 12 }}>
                  <h3 style={{ fontSize: 13, color: 'var(--c-text-muted)', margin: '0 0 4px 0' }}>
                    module：{m.module}
                  </h3>
                  <div className="table-card">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>選取</th>
                          <th style={{ width: '40%' }}>權限 key</th>
                          <th>顯示名稱</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.permissions.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selected.has(p.id)}
                                onChange={() => toggle(p.id)}
                                aria-label={p.key}
                              />
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.key}</td>
                            <td>{p.display_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>
                擁有權限
                <span style={{ color: 'var(--c-text-muted)', fontWeight: 400, marginLeft: 8 }}>
                  （{role.permissions.length} 項）
                </span>
              </h2>
              {role.permissions.length === 0 ? (
                <div className="table-card">
                  <table>
                    <tbody>
                      <tr>
                        <td className="empty-cell">此角色尚未掛載任何權限</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                groupedOwned.map(([module, perms]) => (
                  <div key={module} style={{ marginBottom: 12 }}>
                    <h3 style={{ fontSize: 13, color: 'var(--c-text-muted)', margin: '0 0 4px 0' }}>
                      module：{module}
                    </h3>
                    <div className="table-card">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: '40%' }}>權限 key</th>
                            <th>顯示名稱</th>
                          </tr>
                        </thead>
                        <tbody>
                          {perms.map((p) => (
                            <tr key={p.id}>
                              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.key}</td>
                              <td>{p.display_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
